import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { Send, ArrowLeft, MessageSquare } from "lucide-react";

function fmtTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export default function AdminMessages() {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // thread object
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const wantThread = searchParams.get("thread");

  const fetchThreads = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/messages");
      setThreads(data.threads || []);
      return data.threads || [];
    } catch (e) { console.error("Fetch messages error:", e); toast.error("Failed to load messages."); return []; }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchThreads(); }, [fetchThreads]);

  // Selecting a thread works exactly as before (no read flag changes here; only a reply marks read).
  const openThread = useCallback((t) => { setSelected(t); }, []);

  // Deep link from the notifications bell: /admin/messages?thread=<user_id>
  useEffect(() => {
    if (!wantThread || loading) return;
    const t = threads.find((x) => x.user_id === wantThread);
    if (t) openThread(t);
    setSearchParams({}, { replace: true });
  }, [wantThread, loading, threads, openThread, setSearchParams]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [selected]);

  const sendReply = async () => {
    if (!reply.trim() || !selected) return;
    setSending(true);
    try {
      await api.post("/admin/messages/reply", { user_id: selected.user_id, text: reply.trim() });
      setReply("");
      const updated = await fetchThreads();
      const refreshed = updated.find((t) => t.user_id === selected.user_id);
      if (refreshed) setSelected(refreshed);
      window.dispatchEvent(new Event("mentorai:notifications-refresh"));
      toast.success("Reply sent.");
    } catch (e) { console.error("Reply error:", e); toast.error("Failed to send reply."); }
    finally { setSending(false); }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendReply(); }
  };

  return (
    <div data-testid="admin-messages" className="space-y-6">
      <div>
        <h1 className="font-display text-2xl font-semibold tracking-tight">Messages</h1>
        <p className="mt-1 text-sm text-muted-foreground">Read and respond to client messages.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 min-h-[600px]">
        {/* Thread List */}
        <Card className={`rounded-xl ring-1 ring-black/5 dark:ring-white/5 shadow-sm overflow-hidden lg:col-span-4 ${selected ? "hidden lg:block" : ""}`}>
          <div className="px-4 py-3 border-b bg-muted/20">
            <h2 className="text-sm font-semibold">Threads</h2>
          </div>
          {loading ? (
            <div className="p-4 space-y-3">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}</div>
          ) : threads.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground flex flex-col items-center gap-2">
              <MessageSquare className="size-8 text-muted-foreground/40" />
              No message threads yet.
            </div>
          ) : (
            <ScrollArea className="h-[540px]">
              <div className="divide-y">
                {threads.map((t) => (
                  <button key={t.user_id} data-testid={`thread-${t.user_id}`}
                    onClick={() => openThread(t)}
                    className={`w-full text-left px-4 py-3 hover:bg-muted/40 transition-colors duration-150 ${
                      selected?.user_id === t.user_id ? "bg-muted/50" : ""
                    }`}>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium truncate">{t.user_name}</span>
                      {t.unread > 0 && (
                        <Badge className="rounded-full bg-brand text-white border-0 text-[10px] px-1.5 min-w-[20px] flex items-center justify-center">{t.unread}</Badge>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground truncate mt-0.5">{t.user_email}</div>
                    {t.messages?.length > 0 && (
                      <div className="text-xs text-muted-foreground truncate mt-1 italic">
                        {t.messages[t.messages.length - 1]?.text?.slice(0, 60)}…
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </ScrollArea>
          )}
        </Card>

        {/* Conversation */}
        <Card className={`rounded-xl ring-1 ring-black/5 dark:ring-white/5 shadow-sm overflow-hidden lg:col-span-8 flex flex-col ${!selected ? "hidden lg:flex" : ""}`}>
          {!selected ? (
            <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">
              Select a thread to view messages.
            </div>
          ) : (
            <>
              <div className="px-4 py-3 border-b bg-muted/20 flex items-center gap-3">
                <Button variant="ghost" size="icon" className="lg:hidden size-8" onClick={() => setSelected(null)} data-testid="messages-back">
                  <ArrowLeft className="size-4" />
                </Button>
                <div className="min-w-0">
                  <div className="text-sm font-semibold truncate">{selected.user_name}</div>
                  <div className="text-xs text-muted-foreground truncate">{selected.user_email} · {selected.account_no}</div>
                </div>
              </div>

              <ScrollArea className="flex-1 h-0 min-h-[400px]">
                <div data-testid="messages-thread" className="p-4 space-y-3">
                  {(selected.messages || []).map((m) => (
                    <div key={m.message_id} className={`flex ${m.sender === "client" ? "justify-start" : "justify-end"}`}>
                      <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 ${
                        m.sender === "client"
                          ? "bg-muted text-foreground"
                          : "bg-brand/10 text-foreground ring-1 ring-brand/15"
                      }`}>
                        <div className="text-sm leading-relaxed whitespace-pre-wrap">{m.text}</div>
                        <div className="text-[10px] text-muted-foreground mt-1">
                          {m.sender === "client" ? m.sender_name : "Admin"} · {fmtTime(m.timestamp)}
                        </div>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              </ScrollArea>

              <div data-testid="messages-composer" className="p-3 border-t bg-muted/10">
                <div className="flex gap-2">
                  <Textarea
                    data-testid="admin-reply-input"
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Type your reply… (Enter to send, Shift+Enter for new line)"
                    className="min-h-[44px] max-h-[120px] resize-none rounded-xl text-sm"
                    rows={1}
                  />
                  <Button data-testid="admin-reply-send" onClick={sendReply} disabled={!reply.trim() || sending} size="icon" className="shrink-0 rounded-xl bg-brand text-white hover:bg-[#0B8E8D] size-10">
                    <Send className="size-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
